import { useEffect, useState } from 'react'

import { useAppSelector } from 'store/hooks'
import { selectedUser } from 'store/reducers/users'

import { ReactSVG } from 'react-svg'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import KYCPopup from 'components/ui/account/loan-calculator/components/kyc-popup'

import { createApplicant, getKYCLink, getUserKycStatus } from 'store/fetchers/users'

import { KycStatuses } from 'store/fetchers/users/types'

import ToggleSwitch from '../../toggle-switch'

import Tooltip from '../../../global/tooltip'

import Notice from '../../notice'

import { AddSecondCredPopup } from './add-second-cred-popup'
import styles from './styles.module.scss'

import EditIcon from './icons/icon-edit.svg'
import PlusIcon from './icons/plus.svg'
import { ChangeEmailPopup } from './change-email-popup'
import { ChangePhonePopup } from './change-phone-popup'
import { ChangeBackupEmailPopup } from './change-backup-email-popup'
import { AddBackupEmailPopup } from './add-backup-email-popup'
import Shield from './icons/shield.svg'
import PendingShield from './icons/pending-shield.svg'
import PositiveShield from './icons/positive-shield.svg'
import Arrow from './icons/arrow-right.svg'
import BlueArrow from './icons/blue-arrow-right.svg'
import KycIcon from './icons/kyc.svg'
import X from './icons/X.svg'

export default function AccountInfo({ onClickEnableBtn, onClickUnsetBtn }) {
  const { t } = useTranslation()

  const user = useAppSelector(selectedUser)
  const [kycLink, setKycLink] = useState<string>('')
  const [isPopupOpened, setIsPopupOpened] = useState(false)
  const [isEditPhonePopupOpened, setIsEditPhonePopupOpened] = useState<boolean>(false)
  const [isEditEmailPopupOpened, setIsEditEmailPopupOpened] = useState<boolean>(false)
  const [isBackupEmailPopupOpened, setIsBackupEmailPopupOpened] = useState<boolean>(false)
  const [isEditBackupEmailPopupOpened, setIsEditBackupEmailPopupOpened] = useState<boolean>(false)
  const [isKycVerificationPopupOpened, setIsKycVerificationPopupOpened] = useState<boolean>(false)
  const [isKycSupportPopupOpened, setIsKycSupportPopupOpened] = useState<boolean>(false)
  const [isKycRejectOpened, setIsKycRejectOpened] = useState<boolean>(false)
  const [isUserVerified, setIsUserVerified] = useState<boolean>(false)
  const [kycPending, setKycPending] = useState<boolean>(false)
  const [isKycRejected, setIsKycRejected] = useState<boolean>(false)
  const [isKycRejectedWithRetry, setIsKycRejectedWithRetry] = useState<boolean>(false)
  const [isTriedKycAgain, setIsTriedKycAgain] = useState<boolean>(false)

  useEffect(() => {
    if (isTriedKycAgain && kycLink) {
      verifyKYC()
      setIsTriedKycAgain(false) // Сброс состояния
    }
  }, [isTriedKycAgain, kycLink])

  useEffect(() => {
    const getSumsubKycStatus = async () => {
      try {
        const data = await getUserKycStatus()
        if (!data) {
          return
        }

        if (data.status === KycStatuses.VERIFIED) {
          setIsUserVerified(true)
          setKycPending(false)
        } else if (data.status === KycStatuses.VERIFICATION_IN_PROGRESS) {
          setKycPending(true)
          setIsUserVerified(false)
        } else if (data.status === KycStatuses.VERIFICATION_REFUSED) {
          setIsKycRejectedWithRetry(data.retry)
        }
      } catch (error) {
        console.error('Error fetching KYC status:', error)
      }
    }

    const getKycStatus = async () => {
      try {
        const status = user.kyc_verified as KycStatuses
        switch (status) {
          case KycStatuses.VERIFIED:
            setIsUserVerified(true)
            setKycPending(false)
            break
          case KycStatuses.VERIFICATION_IN_PROGRESS:
            setKycPending(true)
            setIsUserVerified(false)
            await getSumsubKycStatus()
            break
          case KycStatuses.VERIFICATION_REFUSED:
            setIsKycRejected(true)
            break
          case KycStatuses.VERIFICATION_REFUSED_WITH_RETRY:
            setIsKycRejected(true)
            setIsKycRejectedWithRetry(true)
            await getSumsubKycStatus()
            break
          default:
            setIsUserVerified(false)
            setKycPending(false)
            break
        }

        if (status === KycStatuses.NOT_VERIFIED || KycStatuses.VERIFICATION_REFUSED_WITH_RETRY) {
          await createApplicant()
          const kycLink = await getKYCLink()
          setKycLink(kycLink)
        }
      } catch (error) {
        console.error('Error fetching KYC status:', error)
      }
    }

    getKycStatus()
  }, [user])

  if (!user) {
    return null
  }

  const verifyKYC = async () => {
    try {
      if (kycLink) {
        window.open(kycLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error(error)
    }
  }

  const closePopup = (): void => setIsPopupOpened(false)
  const closeBackupPopup = (): void => setIsBackupEmailPopupOpened(false)
  const closeEditPhonePopup = (): void => setIsEditPhonePopupOpened(false)
  const closeEditEmailPopup = (): void => setIsEditEmailPopupOpened(false)
  const closeEditBackupEmailPopup = (): void => setIsEditBackupEmailPopupOpened(false)

  const openPopup = (): void => setIsPopupOpened(true)
  const openSetBackupEmail = (): void => setIsBackupEmailPopupOpened(true)
  // const openEditPhonePopup = (): void => setIsEditPhonePopupOpened(true)
  // const openEditEmailPopup = (): void => setIsEditEmailPopupOpened(true)
  const openKycVerificationPopup = (): void => setIsKycVerificationPopupOpened(true)
  const openEditBackupEmailPopup = (): void => setIsEditBackupEmailPopupOpened(true)
  const openKycSupportPopup = (): void => setIsKycSupportPopupOpened(true)
  const openKycRejectPopup = (): void => setIsKycRejectOpened(true)
  const closeKycSupport = (): void => setIsKycSupportPopupOpened(false)

  const hasPhone = !user?.phone ? openPopup : null
  const hasEmail = !user?.email ? openPopup : null
  const hasBackup = !user?.fallback_email ? openSetBackupEmail : null

  const formattedPhone = user?.phone?.startsWith('+') ? user.phone : '+' + user.phone

  const verifyUser = async () => {
    try {
      const data = await getUserKycStatus()
      if (data && user.kyc_verified !== data.status) {
        setKycPending(false)
        setIsUserVerified(false)
        setIsKycRejected(false)
        setKycPending(false)
        setIsKycRejectedWithRetry(false)

        if (data.status === KycStatuses.VERIFIED) {
          setIsUserVerified(true)
        } else if (data.status === KycStatuses.VERIFICATION_IN_PROGRESS) {
          setKycPending(true)
        } else if (data.status === KycStatuses.VERIFICATION_REFUSED) {
          setIsKycRejected(true)
        } else if (data.status === KycStatuses.VERIFICATION_REFUSED_WITH_RETRY) {
          setIsKycRejectedWithRetry(true)
        }

        return
      }

      openKycVerificationPopup()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <section className={styles.info}>
      <div className={cn(styles.data)}>
        <div className={styles.data__item}>
          <span className={styles.header}>{t('phone')}</span>
          <div className={styles.text} onClick={hasPhone}>
            <p className={!user?.phone ? styles.blue : null}>
              {!user?.phone ? (
                <>
                  <ReactSVG src={PlusIcon} className={styles.plusIcon} /> {t('add')}
                </>
              ) : (
                formattedPhone
              )}
            </p>
          </div>
        </div>

        <div className={cn(styles.data__item, styles.data__item_email)}>
          <span className={styles.header}>{t('email')}</span>
          <div className={cn(styles.text, styles.email__text)} onClick={hasEmail}>
            <p className={!user?.email ? styles.blue : null}>
              {!user?.email ? (
                <>
                  <ReactSVG src={PlusIcon} className={styles.plusIcon} />
                  {t('add')}
                </>
              ) : user?.email}
              {user?.email && user?.email.length > 18 && <Tooltip className={styles.tooltip}>{user.email}</Tooltip>}
            </p>
          </div>
        </div>

        <div className={styles.data__item}>
          <span className={styles.header}>KYC</span>
          {!isUserVerified && !kycPending && !isKycRejected && (
            <div className={styles.text}>
              <p className={styles.blue} onClick={verifyUser}>
                <ReactSVG src={Shield} className={styles.plusIcon} /> {t('verify_kyc')}
              </p>
            </div>
          )}

          {isUserVerified && !kycPending && !isKycRejected && (
            <div className={styles.text}>
              <p className={styles.green}>
                <ReactSVG src={PositiveShield} className={styles.plusIcon} /> {t('kyc_verified')}
              </p>
            </div>
          )}

          {kycPending && !isKycRejected && (
            <div className={styles.text}>
              <button className={styles.iconBtn} onClick={openKycSupportPopup}>
                <p className={styles.orange}>
                  <ReactSVG src={PendingShield} className={styles.plusIcon} /> {t('in_progress')} <img src={Arrow} />
                </p>
              </button>
            </div>
          )}

          {isKycRejected && (
            <div className={styles.text}>
              {isKycRejectedWithRetry ? (
                <>
                  <p className={styles.orange__reject}>{t('rejected')}</p>
                  <button className={styles.iconBtn} onClick={verifyKYC}>
                    <p className={styles.blue}>
                      {t('try_again')}
                      <img src={BlueArrow} alt={'blue arrow'} />
                    </p>
                  </button>
                </>
              ) : (
                <>
                  <button className={styles.iconBtn__reject} onClick={openKycRejectPopup}>
                    <img src={X} alt={'X'} />
                    <p className={styles.orange}>{t('rejected')}</p>
                    <img src={Arrow} alt={'arrow'} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {isKycVerificationPopupOpened && (
        <KYCPopup
          setIsOpened={setIsKycVerificationPopupOpened}
          subtitle={''}
          buttonText={t('kyc_verification')}
          onClick={verifyKYC}
          text={t('kyc_text')}
        />
      )}

      {isKycSupportPopupOpened && (
        <KYCPopup
          setIsOpened={setIsKycSupportPopupOpened}
          subtitle={''}
          title={t('kyc_in_progress')}
          buttonText={t('got_it')}
          onClick={closeKycSupport}
          text={t('kyc_in_progress_text')}
          whiteButtonText={t('support')}
        />
      )}

      {isKycRejectOpened && (
        <KYCPopup
          icon={KycIcon}
          setIsOpened={setIsKycRejectOpened}
          subtitle={''}
          title={t('kyc_reject')}
          buttonText={t('support')}
          text={t('kyc_reject_text')}
        />
      )}

      <div className={styles.data}>
        <div className={styles.data__header}>
          <h3>{t('authenticator_app')}</h3>

          <ToggleSwitch
            className={styles.button}
            toggled={user?.isActive2Fa}
            onToggleOn={onClickEnableBtn}
            onToggleOff={onClickUnsetBtn}
          />
        </div>

        <span
          className={cn(
            styles.backup__header,
            { [styles.black]: !user.fallback_email },
            { [styles.flex]: user.fallback_email },
          )}
        >
          {t('backup_email')}{' '}
          {!user.fallback_email && <span className={styles.moreInfo}>{t('allow_regation_access_2fa')}</span>}
          {user.fallback_email && <Notice alt="backup email description">{t('add_2fa')}</Notice>}
        </span>
        <div className={styles.backup__email} onClick={hasBackup}>
          <p className={!user?.fallback_email ? styles.blue : null}>
            {!user?.fallback_email ? (
              <>
                <ReactSVG src={PlusIcon} className={styles.plusIcon} />
                {t('add_backup_email')}
              </>
            ) : (
              user?.fallback_email
            )}
          </p>
          {user?.fallback_email && (
            <button className={styles.iconBtn} onClick={openEditBackupEmailPopup}>
              <ReactSVG src={EditIcon} className={styles.icon} />
            </button>
          )}
        </div>
      </div>
      {isPopupOpened && <AddSecondCredPopup closePopup={closePopup} hasPhone={!!user.phone} />}
      {isBackupEmailPopupOpened && <AddBackupEmailPopup closePopup={closeBackupPopup} />}
      {isEditEmailPopupOpened && <ChangeEmailPopup closePopup={closeEditEmailPopup} />}
      {isEditBackupEmailPopupOpened && <ChangeBackupEmailPopup closePopup={closeEditBackupEmailPopup} />}
      {isEditPhonePopupOpened && <ChangePhonePopup closePopup={closeEditPhonePopup} />}
    </section>
  )
}
